import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { gsrReportApi, areaPositionApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * Reports — single page that adapts to the user's role.
 *
 *   - is_gsr=true        → shows GSR Report section with celebrations
 *   - is_area_admin=true → shows Area Officer Report section
 *   - both flags         → shows both sections stacked
 *   - neither            → friendly "no access" message
 *
 * Multi-name support: each form has a "Submitted by" field that prefills
 * with the role-specific name from the user's account (gsr_name or
 * area_admin_name), falling back to user.name. The submitter can override
 * the prefill if a different person is filling in for them. The chosen
 * name is snapshotted onto the report so history reflects who actually
 * submitted, even if the account's name fields change later.
 */
export default function Reports() {
    const { user } = useAuth()
    const isGsr = user?.isGsr === true
    const isAreaAdmin = user?.isAreaAdmin === true
    const hasReporting = isGsr || isAreaAdmin
    const noCustomer = !user?.customerId
    const [positions, setPositions] = useState([])

    useEffect(() => {
        if (hasReporting && isAreaAdmin) loadPositions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasReporting])

    async function loadPositions() {
        try {
            const response = await areaPositionApi.list()
            if (response.success) setPositions(response.positions)
        } catch { /* non-fatal */ }
    }

    // ---- No-access state ----------------------------------------------------
    if (!hasReporting) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">No reports for your role</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Monthly reports are submitted by GSRs (Group Service Representatives)
                    and area officers. If you should have access, talk to the literature chair.
                </p>
            </div>
        )
    }

    if (noCustomer) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Account not linked yet</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Your account isn't linked to a customer yet, so you can't submit
                    a report. Reach out to the literature chair to be linked.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Reports</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Submit your monthly report. A PDF copy is emailed to the area
                        secretary on submission.
                    </p>
                </div>
                <Link to="/reports/history">
                    <Button variant="secondary">View Past Reports</Button>
                </Link>
            </div>

            {/* ----- GSR Report --------------------------------------------------- */}
            {isGsr && (
                <GsrReportForm user={user} />
            )}

            {/* ----- Area Officer Report ----------------------------------------- */}
            {isAreaAdmin && (
                <AreaReportForm user={user} positions={positions} />
            )}
        </div>
    )
}

// ============================================================================
//  GSR Report form
// ============================================================================
function GsrReportForm({ user }) {
    // Default submitter name = gsr_name, fall back to account name
    const defaultSubmitterName = user.gsrName || user.name || ''

    const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10))
    const [forTheMonth, setForTheMonth] = useState(getDefaultMonth)
    const [submitterName, setSubmitterName] = useState(defaultSubmitterName)
    const [comments, setComments] = useState('')
    const [celebrations, setCelebrations] = useState([])
    const [busy, setBusy] = useState(false)

    function addCelebration() {
        setCelebrations(cs => [
            ...cs,
            { name: '', noOfYears: '', celebrationDate: '', postPublicly: true, _key: Date.now() + Math.random() }
        ])
    }

    function updateCelebration(idx, field, value) {
        setCelebrations(cs => cs.map((c, i) => i === idx ? { ...c, [field]: value } : c))
    }

    function removeCelebration(idx) {
        setCelebrations(cs => cs.filter((_, i) => i !== idx))
    }

    async function handleSubmit() {
        if (!forTheMonth.trim()) {
            toast.error('Please pick the month this report is for')
            return
        }
        if (!submitterName.trim()) {
            toast.error('Please enter the GSR\'s name')
            return
        }

        for (const c of celebrations) {
            if (!c.name.trim() || !c.celebrationDate || c.noOfYears === '') {
                toast.error('Each celebration needs a name, years, and date')
                return
            }
        }

        setBusy(true)
        try {
            const payload = {
                reportDate,
                forTheMonth: forTheMonth.trim(),
                submitterName: submitterName.trim(),
                budgetAmount: null,
                comments: comments.trim() || null,
                celebrations: celebrations.map(c => ({
                    name: c.name.trim(),
                    noOfYears: Number(c.noOfYears),
                    celebrationDate: c.celebrationDate,
                    postPublicly: !!c.postPublicly,
                })),
            }

            const response = await gsrReportApi.submitGsr(payload)
            if (response.success) {
                toast.success('GSR report submitted')
                // Reset form to fresh state for next month
                setReportDate(new Date().toISOString().slice(0, 10))
                setForTheMonth(getDefaultMonth())
                setSubmitterName(defaultSubmitterName)
                setComments('')
                setCelebrations([])
            } else {
                toast.error(response.message || 'Submission failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-ghasc-navy">GSR Report</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                    Group: <span className="text-gray-700">{user.customerName || '—'}</span>
                </p>
            </div>

            <div className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                        id="gsr-date"
                        label="Report Date"
                        type="date"
                        value={reportDate}
                        onChange={(e) => setReportDate(e.target.value)}
                    />
                    <div>
                        <label htmlFor="gsr-month" className="block text-sm font-medium text-gray-700 mb-1">
                            For the Month
                        </label>
                        <select
                            id="gsr-month"
                            value={forTheMonth}
                            onChange={(e) => setForTheMonth(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                        >
                            {buildMonthOptions().map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <Input
                    id="gsr-submitter"
                    label="Submitted by (GSR name)"
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                />

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                    <textarea
                        rows={4}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="How is your group doing this month?"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    />
                </div>

                {/* ---- Celebrations subsection --- */}
                <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-700">Celebrations</h4>
                        <button
                            type="button"
                            onClick={addCelebration}
                            className="text-sm text-ghasc-navy hover:underline"
                        >
                            + Add Celebration
                        </button>
                    </div>

                    {celebrations.length === 0 ? (
                        <p className="text-xs text-gray-500">
                            No celebrations to report this month. Click "Add Celebration" to add one.
                        </p>
                    ) : (
                            <div className="space-y-2">
                                {celebrations.map((c, i) => (
                                    <div key={c._key} className="bg-gray-50 rounded-lg p-3 space-y-2">
                                        {/* Name — full width on phone */}
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-0.5">Name</label>
                                            <input
                                                type="text"
                                                value={c.name}
                                                onChange={(e) => updateCelebration(i, 'name', e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                            />
                                        </div>
                                        {/* Years + Date side-by-side */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-0.5">Years</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={c.noOfYears}
                                                    onChange={(e) => updateCelebration(i, 'noOfYears', e.target.value)}
                                                    className="no-spinner w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-0.5">Date</label>
                                                <input
                                                    type="date"
                                                    value={c.celebrationDate}
                                                    onChange={(e) => updateCelebration(i, 'celebrationDate', e.target.value)}
                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                />
                                            </div>
                                        </div>
                                        {/* Post publicly + remove */}
                                        <div className="flex items-center justify-between gap-2">
                                            <label className="flex items-center text-xs text-gray-700 flex-1">
                                                <input
                                                    type="checkbox"
                                                    checked={c.postPublicly}
                                                    onChange={(e) => updateCelebration(i, 'postPublicly', e.target.checked)}
                                                    className="mr-2"
                                                />
                                                Okay to post publicly
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => removeCelebration(i)}
                                                className="px-3 py-1.5 text-sm text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100"
                                                title="Remove"
                                            >
                                                ✕ Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                    )}
                </div>

                <div className="flex justify-end pt-2">
                    <Button onClick={handleSubmit} loading={busy}>Submit GSR Report</Button>
                </div>
            </div>
        </div>
    )
}

// ============================================================================
//  Area Officer Report form
// ============================================================================
function AreaReportForm({ user, positions }) {
    // Default submitter name = area_admin_name, fall back to account name
    const defaultSubmitterName = user.areaAdminName || user.name || ''

    const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10))
    const [forTheMonth, setForTheMonth] = useState(getDefaultMonth)
    const [position, setPosition] = useState(user?.areaPosition || '')
    const [submitterName, setSubmitterName] = useState(defaultSubmitterName)
    const [budgetAmount, setBudgetAmount] = useState('')
    const [comments, setComments] = useState('')
    const [busy, setBusy] = useState(false)

    async function handleSubmit() {
        if (!forTheMonth.trim()) {
            toast.error('Please pick the month this report is for')
            return
        }
        if (!position.trim()) {
            toast.error('Please pick a position')
            return
        }
        if (!submitterName.trim()) {
            toast.error('Please enter the officer\'s name')
            return
        }

        setBusy(true)
        try {
            const payload = {
                reportDate,
                forTheMonth: forTheMonth.trim(),
                position: position.trim(),
                submitterName: submitterName.trim(),
                budgetAmount: budgetAmount === '' ? null : Number(budgetAmount),
                comments: comments.trim() || null,
            }

            const response = await gsrReportApi.submitArea(payload)
            if (response.success) {
                toast.success('Area report submitted')
                setReportDate(new Date().toISOString().slice(0, 10))
                setForTheMonth(getDefaultMonth())
                setSubmitterName(defaultSubmitterName)
                setBudgetAmount('')
                setComments('')
            } else {
                toast.error(response.message || 'Submission failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-ghasc-navy">Area Officer Report</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                    Area: <span className="text-gray-700">{user.customerName || '—'}</span>
                </p>
            </div>

            <div className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                        id="area-date"
                        label="Report Date"
                        type="date"
                        value={reportDate}
                        onChange={(e) => setReportDate(e.target.value)}
                    />
                    <div>
                        <label htmlFor="area-month" className="block text-sm font-medium text-gray-700 mb-1">
                            For the Month
                        </label>
                        <select
                            id="area-month"
                            value={forTheMonth}
                            onChange={(e) => setForTheMonth(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                        >
                            {buildMonthOptions().map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <Input
                    id="area-submitter"
                    label="Submitted by (officer's name)"
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                />

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                    <select
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">-- Select position --</option>
                        {positions.map(p => (
                            <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                        {user?.areaPosition && !positions.find(p => p.name === user.areaPosition) && (
                            <option value={user.areaPosition}>{user.areaPosition} (your role)</option>
                        )}
                    </select>
                </div>

                <Input
                    id="area-budget"
                    label="Budget Used ($)"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={budgetAmount}
                    onChange={(e) => setBudgetAmount(e.target.value)}
                />

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                    <textarea
                        rows={5}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="What's happening in your service position this month?"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    />
                </div>

                <div className="flex justify-end pt-2">
                    <Button onClick={handleSubmit} loading={busy}>Submit Area Report</Button>
                </div>
            </div>
        </div>
    )
}

function Field({ label, value }) {
    return (
        <div>
            <span className="text-xs text-gray-500">{label}:</span>{' '}
            <span className="text-gray-700">{value}</span>
        </div>
    )
}

// ============================================================================
//  Helpers
// ============================================================================
function getDefaultMonth() {
    // Default "For the Month" to the previous month — that's typically what
    // a report submitted today is reporting on.
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    return formatMonth(d)
}

/**
 * Build the list of months for the "For the Month" dropdown.
 * Range: 1 month forward + current month + 6 months back = 8 options.
 */
function buildMonthOptions() {
    const months = []
    const now = new Date()
    for (let offset = 1; offset >= -6; offset--) {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
        months.push(formatMonth(d))
    }
    return months
}

function formatMonth(d) {
    const monthName = d.toLocaleDateString('en-US', { month: 'long' })
    return `${monthName} ${d.getFullYear()}`
}